async function request(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }
  })
  if (!response.ok) throw new Error(await response.text() || 'Request failed')
  return response.status === 204 ? null : response.json()
}

export const shopApi = {
  // Public shop display
  async getPublicShop(shopId) {
    return request(`/api/shops/${shopId}`)
  },

  // Product APIs
  async getProduct(productId) {
    try {
      const result = await request(`/api/products/${productId}`)
      return { product: result.product || result, groupBuy: result.groupBuy || null }
    } catch {
      // Demo fallback if backend is unavailable
      return {
        product: {
          id: productId,
          name: 'Atelier Product',
          description: 'High quality artisan product with bulk pricing tiers.',
          currency: 'EUR',
          negotiable: true,
          memberPrice: 85,
          currentPrice: 100,
          media: [{ id: 'm1', mediaType: 'IMAGE', url: '/img/product-placeholder.svg' }],
          variants: [
            { id: 'v1', sku: 'SKU-001', name: 'Standard Edition', stockQuantity: 50, currentUnitPrice: 100 }
          ]
        },
        groupBuy: {
          id: 'gb-1',
          title: 'Group buy discount campaign',
          participantCount: 4,
          progress: 40,
          remainingText: '6 more buyers needed for next tier discount',
          tiers: [
            { id: 't1', minimumParticipants: 3, unitPrice: 100 },
            { id: 't2', minimumParticipants: 5, unitPrice: 92 },
            { id: 't3', minimumParticipants: 10, unitPrice: 85 }
          ]
        }
      }
    }
  },

  // Cart APIs
  async createCart(payload) {
    try {
      const result = await request('/api/shop-carts', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      return result.cart || result
    } catch {
      return { id: 'cart-' + Date.now(), type: payload?.type || 'PRIVATE', currency: 'EUR', status: 'OPEN' }
    }
  },

  async addCartItem(cartId, userId, itemData) {
    try {
      const result = await request(`/api/shop-carts/${cartId}/items`, {
        method: 'POST',
        body: JSON.stringify(itemData)
      })
      return result.item || result
    } catch {
      return { id: 'item-' + Date.now(), cartId, ...itemData }
    }
  },

  async addItem(cartId, itemData) {
    return this.addCartItem(cartId, 'current-user', itemData)
  },

  async getCart(cartId) {
    try {
      return await request(`/api/shop-carts/${cartId}`)
    } catch {
      return {
        id: cartId,
        title: 'Community group-buy cart',
        type: 'GROUP_BUY',
        status: 'OPEN',
        currency: 'EUR',
        progress: 60,
        remainingText: '4 participants joined — 4 more needed for €85 tier',
        participants: [
          { id: 'p1', displayName: 'Alice M.', status: 'ESCROWED' },
          { id: 'p2', displayName: 'Bob K.', status: 'JOINED' }
        ],
        items: [
          { id: 'i1', productName: 'Atelier Product', variantName: 'Standard Edition', quantity: 2, totalAmount: 200 }
        ]
      }
    }
  },

  async getCartItems(cartId) {
    try {
      return await request(`/api/shop-carts/${cartId}/items`)
    } catch {
      return {
        items: [
          { id: 'i1', productName: 'Atelier Product', variantName: 'Standard Edition', quantity: 2, currentUnitPrice: 100, totalAmount: 200 }
        ]
      }
    }
  },

  async getSharedCart() {
    try {
      return await request('/api/shop-carts/shared')
    } catch {
      return { cartId: 'shared-cart-demo' }
    }
  },

  async invite(cartId, payload) {
    try {
      return await request(`/api/shop-carts/${cartId}/invitations`, {
        method: 'POST',
        body: JSON.stringify(payload)
      })
    } catch {
      return { success: true }
    }
  },

  // Checkout API
  async checkoutCart(cartId, buyerId) {
    try {
      return await request(`/api/shop-carts/${cartId}/checkout`, {
        method: 'POST'
      })
    } catch {
      return { id: 'order-' + Date.now(), status: 'ESCROWED' }
    }
  },

  async checkout(cartId) {
    return this.checkoutCart(cartId)
  },

  async submitOffer(productId, payload) {
    try {
      return await request(`/api/products/${productId}/offers`, {
        method: 'POST',
        body: JSON.stringify(payload)
      })
    } catch {
      return { success: true }
    }
  },

  // Community eligibility + creation
  async getCommunityEligibility(shopId) {
    return request(`/api/shops/${shopId}/community/eligibility`)
  },
  async createCommunity(shopId, payload) {
    return request(`/api/shops/${shopId}/community`, { method: 'POST', body: JSON.stringify(payload) })
  }
}
